import type { ApiCategory, ApiProduct, ApiTag } from '@/api/types/menu'
import { env } from '@/config/env'
import type { Category, Product, ProductTag } from '@/types/product'
import { getLanguage } from '@/utils/language'
import { stripEmoji } from '@/utils/text'
import { t } from '@/i18n'

/** Картинки приходят по http — в Telegram страница всегда https, иначе их заблокирует. */
export function secureUrl(url: string | null): string {
  return url ? url.replace(/^http:\/\//, 'https://') : ''
}

/**
 * У товаров `imageUrl` — полная ссылка, а у модификаторов только имя файла
 * («4a7c…015fa.jpg»). Такое имя браузер считает относительным и ищет рядом
 * с текущей страницей, поэтому достраиваем путь сами — через свой прокси,
 * чтобы домен бэкенда не уезжал в бандл и не ломался mixed content.
 */
export function imageUrl(value: string | null): string {
  if (!value) return ''
  if (/^https?:\/\//.test(value)) return secureUrl(value)

  return `${env.apiBase}/image/${value.replace(/^\/+/, '')}`
}

/** Единица порции: 0 — граммы, 1 — миллилитры. */
function formatMeasure(measure: number, unit: number): string {
  if (!measure) return ''
  return `${measure} ${unit === 1 ? t('units.ml') : t('units.g')}`
}

/* Имя тега бэкенд отдаёт тремя полями сразу — сводим по текущей локали */
const TAG_NAME: Record<string, keyof Pick<ApiTag, 'name_Ru' | 'name_Uz' | 'name_En'>> = {
  ru: 'name_Ru',
  uz: 'name_Uz',
  en: 'name_En',
}

/** Цвета из CRM приходят не всегда — пустые заменяем на токены темы. */
export function toTag(source: ApiTag): ProductTag {
  const field = TAG_NAME[getLanguage()] ?? 'name_Ru'

  return {
    id: source.tagId,
    name: source[field] || source.name_Ru,
    background: source.backgroundColor || 'var(--surface-muted)',
    color: source.textColor || 'var(--text-color)',
    image: secureUrl(source.imageUrl),
    isGroup: source.isGroup,
    orientation: source.groupOrientation,
    align: source.align,
  }
}

/** Товар бэкенда → модель карточки. */
export function toProduct(source: ApiProduct): Product {
  return {
    id: source.id,
    name: source.name,
    price: source.price,
    weight: formatMeasure(source.measure, source.unit),
    image: secureUrl(source.smallImageUrl ?? source.imageUrl),
    ...(source.oldPrice ? { oldPrice: source.oldPrice, isSale: true } : {}),
    ...(source.isNew ? { isNew: true } : {}),
    ...(source.allTags?.length ? { tags: source.allTags.map(toTag) } : {}),
  }
}

/**
 * Эмодзи в названиях категорий бренд ставит сам, в CRM. Мы их прячем: они
 * дублируют картинку категории и ломают выравнивание в чипах. Флаг здесь,
 * а не в настройках клиента — это решение проекта, а не пользователя.
 */
const KEEP_CATEGORY_EMOJI = false

/** Название категории для чипа и заголовка секции. */
export function categoryLabel(name: string): string {
  return KEEP_CATEGORY_EMOJI ? name.trim() : stripEmoji(name)
}

/** Категория бэкенда → чип в шапке меню. */
export function toCategory(source: ApiCategory): Category {
  return {
    id: source.id,
    label: categoryLabel(source.name),
    image: secureUrl(source.imageUrl),
  }
}
